// CompanyService — a company ADMIN managing THEIR OWN company (self settings + branding), backed by
// AdminCompanyController (/admin/company/*). The server forces the target company to the caller's own
// (the id in the path is a legacy placeholder, never trusted). A company admin can never touch another
// company here. Platform-level company management (create/list/delete companies) is SYSTEM_ADMIN-only
// and lives in ./system/CompanyService.js.
//
// Migrated the pure JSON reads/deletes to the OpenAPI-generated client. The multipart file uploads
// (FormData) and the two delete endpoints whose URL keeps an unfillable `{companyId}` placeholder stay
// on HttpService and are flagged below.

import httpService from './HttpService';
import {
  adminCompanyControllerGetCompany,
  adminCompanyControllerRemoveBrandingLogo,
  adminCompanyControllerRemoveBrandingBackground,
  adminCompanyControllerUpdateSettings,
  adminCompanyControllerUpdateBranding,
} from '../api/generated';

// Preserve the legacy axios rejection shape so call sites reading `e.response.data.errorCode` keep working.
const throwLikeAxios = (error, response) => {
  throw {response: {data: error, status: response?.status}};
};

// The caller's own company (id is the caller's own; scope is taken from the principal server-side).
export async function getRegularCompany(id) {
  const {data, error, response} = await adminCompanyControllerGetCompany({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
}

// Settings toggles for the caller's OWN company (company from the principal server-side, so no id).
//
// A company ADMIN must use THIS, not ./system/CompanyService.js's updateCompanySettings: that one
// hits PUT /system/company/{id}/settings, which is SYSTEM_ADMIN-only — a plain company admin saving
// their own settings got a 401. The reverse also holds: a company-less SYSTEM_ADMIN drilling into
// another company can't use this one (no company on the principal → 403), so the two are NOT
// interchangeable and the caller has to pick by role.
//
// PATCH semantics like its /system twin: omitted fields are left untouched, so send only the toggle
// that changed. Settings-only — it cannot reach branding, white-label, or `enabled`.
export async function updateOwnCompanySettings(settings) {
  const {data, error, response} = await adminCompanyControllerUpdateSettings({body: settings});
  if (error) throwLikeAxios(error, response);
  return {data};
}

// Identity + branding fields for the caller's OWN company (company from the principal server-side,
// so no id).
//
// A company ADMIN must use THIS, not ./system/CompanyService.js's updateCompanyBranding: that one
// hits PUT /system/company/{id}/branding, which is SYSTEM_ADMIN-only — a plain company admin saving
// their own branding got a 401 while the logo/background uploads on the same form (already /admin
// routes) succeeded, half-applying the save. The reverse also holds: a company-less SYSTEM_ADMIN
// drilling into another company can't use this one (no company on the principal → 403), so the two
// are NOT interchangeable and the caller has to pick by role.
//
// PATCH semantics like its /system twin: omitted fields are left untouched. Branding-only — it
// cannot reach the settings toggles or `enabled`. The white-label fields (bundleId, store URLs, min
// versions) are absent from the server's request DTO on this route and are dropped if sent: binding
// a white-label app is SYSTEM_ADMIN-only.
export async function updateOwnCompanyBranding(branding) {
  const {data, error, response} = await adminCompanyControllerUpdateBranding({body: branding});
  if (error) throwLikeAxios(error, response);
  return {data};
}

// Currently UNUSED. The company page's settings + branding cards moved to the by-id endpoints in
// ./system/CompanyService.js (updateCompanySettings / updateCompanyBranding), because this one
// scopes the save to the CALLER's own company — which 403s for a company-less SYSTEM_ADMIN drilling
// into another company. Kept because it remains the correct self-service path for a company ADMIN
// editing their own company; wire it up there rather than reaching for the /system endpoints.
// TODO(openapi-migration): multipart/FormData upload — not modelled by the JSON SDK. Stays on HttpService.
export async function saveCompany(metaData, file) {
  const token = localStorage.getItem("auth");
  const formData = new FormData();

  if (file) {
    let blob = await fetch(file).then(r => r.blob());
    // The blob from a Response has no .name at the type level (only File does); this preserves the
    // pre-migration runtime behavior — the third arg is undefined when absent, which FormData allows.
    formData.append("file", blob, /** @type {any} */ (blob).name);
  }

  formData.append("metaData", metaData);

  return await httpService.post(`admin/company`, formData, token);
}

// TODO(openapi-migration): multipart/FormData upload — not modelled by the JSON SDK. Stays on HttpService.
export const uploadBrandingLogo = async (id, file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();
  formData.append("file", file, file.name);
  return await httpService.post(`admin/company/updateBrandingLogo/${id}`, formData, token);
};

// TODO(openapi-migration): multipart/FormData upload — not modelled by the JSON SDK. Stays on HttpService.
export const uploadBrandingBackground = async (id, file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();
  formData.append("file", file, file.name);
  return await httpService.post(`admin/company/updateBrandingBackground/${id}`, formData, token);
};

export const removeBrandingLogo = async (id) => {
  const {data, error, response} = await adminCompanyControllerRemoveBrandingLogo({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const removeBrandingBackground = async (id) => {
  const {data, error, response} = await adminCompanyControllerRemoveBrandingBackground({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

// TODO(openapi-migration): multipart/FormData upload — not modelled by the JSON SDK. Stays on HttpService.
export const uploadBrandingImage = async (companyId, imageType, imageKey, file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();
  formData.append("file", file, file.name);
  return await httpService.post(
      `admin/company/brandingImage/${companyId}?imageType=${imageType}&imageKey=${imageKey}`,
      formData,
      token);
};

// TODO(openapi-migration): the generated SDK dropped `companyId` from the path type but the URL template
// still contains an unfillable `{companyId}` placeholder (server treats it as a legacy, never-bound
// placeholder). Migrating would leave a literal `{companyId}` in the request path, so this stays on
// HttpService until the server spec drops the placeholder segment.
export const removeBrandingImage = async (companyId, imageType, imageKey) => {
  const token = localStorage.getItem("auth");
  return await httpService.delete(
      `admin/company/brandingImage/${companyId}?imageType=${imageType}&imageKey=${imageKey}`,
      null,
      token);
};

// TODO(openapi-migration): same unfillable `{companyId}` path-placeholder issue as removeBrandingImage.
// Stays on HttpService until the server spec drops the placeholder segment.
export const removeWeekImage = async (companyId, imageKey) => {
  const token = localStorage.getItem("auth");
  return await httpService.delete(
      `admin/company/weekImage/${companyId}/${imageKey}`,
      null,
      token);
};
